import { useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { readStoredTripId, storeTripId } from '../lib/tripQuery';

export type ActiveTrip = {
  id: string;
  project_number: string;
  status: string | null;
  start_date: string | null;
  end_date: string | null;
  scope: string | null;
  po_number: string | null;
  solution_types: string[] | null;
  deployment_id: string | null;
  customer: { id: string; name: string; account_number: string } | null;
  location: {
    id: string;
    name: string;
    address: unknown;
    contact_name: string | null;
    contact_phone: string | null;
    contact_email: string | null;
  } | null;
  lead_technician: { id: string; full_name: string | null; email: string | null } | null;
};

/** Explicit column list: the onsite app never selects `*` on customer-scoped tables. */
const TRIP_COLUMNS = `
  id, project_number, status, start_date, end_date, scope, po_number, solution_types, deployment_id,
  customer:customers(id, name, account_number),
  location:locations(id, name, address, contact_name, contact_phone, contact_email),
  lead_technician:users!field_trips_lead_technician_id_fkey(id, full_name, email)
`;

export const ACTIVE_TRIP_STATUSES = ['scheduled', 'in_progress'] as const;

export function useActiveTrip() {
  const [searchParams, setSearchParams] = useSearchParams();
  const paramTrip = searchParams.get('trip');
  const paramDeployment = searchParams.get('deployment');

  const query = useQuery({
    queryKey: ['active-trips'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('field_trips')
        .select(TRIP_COLUMNS)
        .in('status', [...ACTIVE_TRIP_STATUSES])
        .order('start_date', { ascending: false });

      if (error) throw error;
      return (data ?? []) as unknown as ActiveTrip[];
    },
    staleTime: 2 * 60 * 1000,
  });

  const trips = useMemo(() => query.data ?? [], [query.data]);
  const storedId = readStoredTripId();

  const selected = useMemo(() => {
    if (trips.length === 0) return null;
    const fromParam = paramTrip ? trips.find((t) => t.id === paramTrip) : undefined;
    if (fromParam) return fromParam;
    const fromDeployment = paramDeployment
      ? trips.find((t) => t.deployment_id === paramDeployment)
      : undefined;
    if (fromDeployment) return fromDeployment;
    const fromStore = storedId ? trips.find((t) => t.id === storedId) : undefined;
    if (fromStore) return fromStore;
    if (trips.length === 1) return trips[0];
    return null;
  }, [trips, paramTrip, paramDeployment, storedId]);

  const selectTrip = useCallback(
    (id: string) => {
      storeTripId(id);
      const next = new URLSearchParams(searchParams);
      next.set('trip', id);
      next.delete('deployment');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  return {
    ...query,
    data: selected,
    trips,
    selectTrip,
  };
}

/**
 * Most recently completed trips, shown read-only when nothing is scheduled
 * or in progress so the app never opens on a blank screen.
 */
export function useRecentTrips(enabled: boolean, limit = 10) {
  return useQuery({
    queryKey: ['recent-trips', limit],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('field_trips')
        .select(TRIP_COLUMNS)
        .eq('status', 'completed')
        .order('end_date', { ascending: false, nullsFirst: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []) as unknown as ActiveTrip[];
    },
    staleTime: 5 * 60 * 1000,
  });
}
